import { V3Models } from "@istari/istari-client";
import { useCallback } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";

import type { ChatTranscriptItem } from "~/ai-chat/model/chat-message";
import { chatResourceFilename, chatTranscriptToMarkdown } from "~/ai-chat/model/chat-transcript-markdown";
import { useAssignInfosecLevel } from "~/api/useInfosecLevels";
import { useCreateResource } from "~/resource/data-source/use-create-resource";
import { buildResourcePath } from "~/routes";

/**
 * Save the current chat transcript as a Markdown resource in the user's library
 * with the chosen infosec level, then toast a link to the created file. When
 * `onShare` is given, the toast also offers a Share action for the new resource.
 */
export function useSaveChatResource(onShare?: (resourceId: string, displayName: string) => void) {
  const createResource = useCreateResource();
  const assignInfosecLevel = useAssignInfosecLevel();
  const navigate = useNavigate();

  const save = useCallback(
    async (title: string, items: ChatTranscriptItem[], infosecLevelId?: string): Promise<boolean> => {
      const displayName = title.trim() || "Istari chat";
      const markdown = chatTranscriptToMarkdown(displayName, items);
      const file = new File([markdown], chatResourceFilename(displayName), { type: "text/markdown" });

      try {
        // Model, not File: the Files explorer lists only Model/Artifact resources,
        // so a File-typed saved chat would be invisible in the library.
        const resource = await createResource.mutateAsync({
          file,
          displayName,
          resourceType: V3Models.ResourceTypeDto.Model,
        });

        let infosecApplied = true;
        if (infosecLevelId) {
          // The resource already exists here, so a failed level assignment must not
          // reject — a caller retry would create a duplicate. Surface the miss so
          // the user can set it from the resource page.
          try {
            await assignInfosecLevel.mutateAsync({ type: "model", id: resource.resourceId, infosecLevelId });
          } catch {
            infosecApplied = false;
          }
        }

        const openResource = () => navigate(buildResourcePath({ resourceId: resource.resourceId }));
        toast.success(
          infosecApplied
            ? "Chat saved to your library."
            : "Chat saved, but the infosec level couldn't be applied. Set it from the resource page.",
          onShare
            ? {
                action: { label: "Share", onClick: () => onShare(resource.resourceId, displayName) },
                cancel: { label: "Open", onClick: openResource },
              }
            : { action: { label: "Open", onClick: openResource } },
        );
        return true;
      } catch {
        toast.error("Couldn't save the chat. Please try again.");
        return false;
      }
    },
    [createResource, assignInfosecLevel, navigate, onShare],
  );

  return { save, isSaving: createResource.isPending || assignInfosecLevel.isPending };
}
